<template>
  <section id="contact" class="py-24 bg-gray-900" ref="contactSection">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-16" ref="contactTitle">
        <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('contact.subtitle') }}</span>
        <h2 class="contact-title font-heading tracking-tight mt-4 font-bold text-white">{{ $t('contact.title') }}</h2>
        <p class="text-lg sm:text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed text-justify">Have questions or want to discuss your IT needs? We're here to help.</p>
      </div>

      <div class="max-w-3xl mx-auto">
        <!-- Contact Form -->
        <div
          class="bg-gray-800 rounded-2xl shadow-lg p-8"
          ref="contactForm"
          :class="{ 'animate-fade-up': formVisible }"
        >
          <form @submit.prevent="submitForm" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="form-group">
                <label for="name" class="form-label">{{ $t('contact.form.firstName') }}</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  </div>
                  <input type="text" id="name" v-model="form.name" required 
                         class="form-input pl-10" placeholder="John Doe">
                </div>
              </div>
              <div class="form-group">
                <label for="email" class="form-label">{{ $t('contact.form.email') }}</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                   
                  </div>
                  <input type="email" id="email" v-model="form.email" required 
                         class="form-input pl-10" placeholder="john@example.com">
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="subject" class="form-label">{{ $t('contact.form.subject') }}</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  
                </div>
                <input type="text" id="subject" v-model="form.subject" required 
                       class="form-input pl-10" placeholder="How can we help?">
              </div>
            </div>

            <div class="form-group">
              <label for="serviceType" class="form-label">{{ $t('contact.form.serviceType') || 'What type of services' }}</label>
              <div class="relative">
                <select id="serviceType" v-model="form.serviceType" required
                        class="form-input pl-10 bg-gray-700">
                  <option value="">{{ $t('contact.form.selectService') || 'Select a service' }}</option>
                  <option v-for="service in availableServices" :key="service.key" :value="service.title">
                    {{ service.title }}
                  </option>
                </select>
              </div>
              <div v-if="errorMessage && errorMessage.includes('service type')" class="text-red-600 text-sm mt-1">{{ errorMessage }}</div>
            </div>

            <div class="form-group">
              <label for="message" class="form-label">{{ $t('contact.form.message') }}</label>
              <div class="relative">
                <div class="absolute top-3 left-3">
                  
                </div>
                <textarea id="message" v-model="form.message" required rows="6" 
                          class="form-input pl-10 pt-3" placeholder="Tell us about your project..."></textarea>
              </div>
            </div>

            <button type="submit" 
                    class="btn-primary w-full py-4 text-lg font-medium flex items-center justify-center group">
              {{ $t('contact.form.submit') }}
              <svg class="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
            <!-- reCAPTCHA Protection Badge -->
            <div class="flex items-center justify-center mt-3 text-xs text-gray-500">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
              <span>Protected by reCAPTCHA</span>
            </div>
            <div v-if="successMessage" class="text-green-600 text-center mt-4">{{ successMessage }}</div>
            <div v-if="errorMessage" class="text-red-600 text-center mt-4">{{ errorMessage }}</div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { textReveal } from '../utils/animations'
import { useRecaptcha } from '../composables/useRecaptcha'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'

const { t, locale } = useI18n()
const { executeRecaptcha } = useRecaptcha()
const contactSection = ref(null)
const contactTitle = ref(null)
const contactForm = ref(null)

const form = ref({
  name: '',
  email: '',
  subject: '',
  serviceType: '',
  message: ''
})

const successMessage = ref('');
const errorMessage = ref('');

const formVisible = ref(false)

// Rate limiting
const lastSubmissionTime = ref(null)
const submissionCount = ref(0)
const RATE_LIMIT_WINDOW = 60000 // 1 minute
const MAX_SUBMISSIONS_PER_WINDOW = 3

const checkRateLimit = () => {
  const now = Date.now()
  if (lastSubmissionTime.value && (now - lastSubmissionTime.value) > RATE_LIMIT_WINDOW) {
    submissionCount.value = 0
  }
  if (submissionCount.value >= MAX_SUBMISSIONS_PER_WINDOW) {
    const timeRemaining = Math.ceil((RATE_LIMIT_WINDOW - (now - lastSubmissionTime.value)) / 1000)
    throw new Error(`Too many submissions. Please wait ${timeRemaining} seconds before trying again.`)
  }
  lastSubmissionTime.value = now
  submissionCount.value++
}

// Get available services from translations
const availableServices = computed(() => {
  try {
    const services = []
    const currentLocale = locale.value || 'en'
    // Handle both default export and direct import
    const enMessages = enTranslations.default || enTranslations
    const deMessages = deTranslations.default || deTranslations
    const messages = currentLocale === 'en' ? enMessages : deMessages
    
    // Get services from servicesList
    const servicesList = messages?.services?.servicesList
    if (servicesList && typeof servicesList === 'object') {
      Object.keys(servicesList).forEach(key => {
        const service = servicesList[key]
        if (service && typeof service === 'object' && service.title) {
          services.push({ key, title: service.title })
        }
      })
    }
    
    // Get services from items
    const items = messages?.services?.items
    if (items && typeof items === 'object') {
      Object.keys(items).forEach(key => {
        const service = items[key]
        if (service && typeof service === 'object' && service.title) {
          services.push({ key, title: service.title })
        }
      })
    }
    
    // Get service models
    const serviceModels = messages?.services?.serviceModels
    if (serviceModels && typeof serviceModels === 'object') {
      if (serviceModels.flatRate && serviceModels.flatRate.title) {
        services.push({ key: 'flatRate', title: serviceModels.flatRate.title })
      }
      if (serviceModels.activityBased && serviceModels.activityBased.title) {
        services.push({ key: 'activityBased', title: serviceModels.activityBased.title })
      }
    }
    
    // Fallback services if translations fail
    if (services.length === 0) {
      console.warn('No services found in translations, using fallback')
      return [
        { key: 'cloud', title: 'Cloud Solutions' },
        { key: 'resourceTeams', title: 'Resource or Teams as Services' },
        { key: 'itSupport', title: 'IT Support' },
        { key: 'optimization', title: 'Process Optimization' },
        { key: 'infrastructure', title: 'On Demand Infrastructure Services' },
        { key: 'project', title: 'Project Management Support' },
        { key: 'data', title: 'Data Management and Analytics' },
        { key: 'dispatch', title: 'Resource or Team as Service' },
        { key: 'flatRate', title: 'Flat-Rate Basic Health Check Service' },
        { key: 'activityBased', title: 'Activity-Based Customised Service' }
      ]
    }
    
    return services
  } catch (e) {
    console.error('Error loading services:', e)
    // Return fallback services on error
    return [
      { key: 'cloud', title: 'Cloud Solutions' },
      { key: 'resourceTeams', title: 'Resource or Teams as Services' },
      { key: 'itSupport', title: 'IT Support' },
      { key: 'optimization', title: 'Process Optimization' },
      { key: 'infrastructure', title: 'On Demand Infrastructure Services' },
      { key: 'project', title: 'Project Management Support' },
      { key: 'data', title: 'Data Management and Analytics' },
      { key: 'dispatch', title: 'Resource or Team as Service' },
      { key: 'flatRate', title: 'Flat-Rate Basic Health Check Service' },
      { key: 'activityBased', title: 'Activity-Based Customised Service' }
    ]
  }
})

const validateForm = () => {
  const errors = {}
  if (!form.value.name || form.value.name.trim().length < 2) {
    errors.name = 'Name is required'
  }
  if (!form.value.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.email = 'Valid email is required'
  }
  if (!form.value.subject || form.value.subject.trim().length < 3) {
    errors.subject = 'Subject is required'
  }
  if (!form.value.serviceType || form.value.serviceType.trim().length === 0) {
    errors.serviceType = 'Please select a service type'
  }
  if (!form.value.message || form.value.message.trim().length < 10) {
    errors.message = 'Message is required (minimum 10 characters)'
  }
  return errors
}


const submitForm = async () => {
  successMessage.value = '';
  errorMessage.value = '';
  
  // Validate form
  const errors = validateForm()
  if (Object.keys(errors).length > 0) {
    errorMessage.value = Object.values(errors)[0]
    return
  }
  
  // Check rate limiting
  try {
    checkRateLimit()
  } catch (rateLimitError) {
    errorMessage.value = rateLimitError.message
    return
  }
  
  try {
    // Get reCAPTCHA token (optional)
    let recaptchaToken = null
    try {
      recaptchaToken = await executeRecaptcha('contact_form_submit')
    } catch (recaptchaError) {
      console.warn('reCAPTCHA error (continuing anyway):', recaptchaError)
    }

    // Sanitize form data
    const sanitizedForm = {
      name: form.value.name.trim().substring(0, 100),
      email: form.value.email.trim().substring(0, 255),
      subject: form.value.subject.trim().substring(0, 200),
      serviceType: form.value.serviceType.trim().substring(0, 200),
      message: form.value.message.trim().substring(0, 5000),
      recaptchaToken: recaptchaToken,
      timestamp: new Date().toISOString()
    }

    // Try to submit to API if available, otherwise simulate submission
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/contact'
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        successMessage.value = data.message || 'Your message has been sent successfully!';
        form.value = { name: '', email: '', subject: '', message: '' };
        setTimeout(() => {
          successMessage.value = '';
        }, 5000);
        return;
      } else {
        // If API endpoint doesn't exist, fall through to simulation
        const data = await response.json().catch(() => ({}));
        if (data.error) {
          errorMessage.value = data.error;
          submissionCount.value = Math.max(0, submissionCount.value - 1);
          return;
        }
      }
    } catch (fetchError) {
      // API endpoint doesn't exist or network error - simulate submission
      console.log('API endpoint not available, simulating form submission:', fetchError);
    }

    // Simulate form submission (when API is not available)
    await new Promise(resolve => setTimeout(resolve, 1000));
    successMessage.value = 'Your message has been sent successfully! We will get back to you shortly.';
    form.value = { name: '', email: '', subject: '', serviceType: '', message: '' };
    setTimeout(() => {
      successMessage.value = '';
    }, 5000);
  } catch (err) {
    console.error('Form submission error:', err);
    errorMessage.value = 'An error occurred. Please try again later.';
    submissionCount.value = Math.max(0, submissionCount.value - 1);
  }
}

onMounted(() => {
  const contactObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        textReveal(contactTitle.value, 0)
        formVisible.value = true
        textReveal(contactForm.value, 200)
      }
    })
  }, { threshold: 0.2 })
  contactObserver.observe(contactSection.value)
})
</script>

<style scoped>
@keyframes fade-up {
  0% {
    opacity: 0;
    transform: translateY(40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fade-up {
  animation: fade-up 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-gray-300;
}

.form-input {
  @apply w-full px-4 py-3 rounded-xl border border-gray-600 bg-gray-700 text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200;
}

.form-input:focus {
  @apply border-primary;
}

.btn-primary {
  @apply bg-primary text-white hover:bg-primary-dark focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200;
}

.group:hover .form-input {
  @apply border-primary/50;
}

.group:hover .social-icon {
  @apply text-primary;
}

.contact-title {
  @apply text-xl md:text-2xl font-bold;
}
</style> 